import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { subscribeOffline } from "@/lib/offline";

const RED = "#EF4444";

// Full-screen drawing layer: draw any rough line, it snaps straight, then reports its Y.
export default function DrawDividerOverlay({ onDrawn, onCancel }) {
  const [points, setPoints] = useState([]);
  const [straight, setStraight] = useState(null);
  const [barShown, setBarShown] = useState(false);
  useEffect(() => subscribeOffline((s) => setBarShown(!s.online || s.syncing)), []);
  const drawing = useRef(false);

  const start = (e) => {
    if (straight) return;
    drawing.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setPoints([[e.clientX, e.clientY]]);
  };
  const move = (e) => {
    if (!drawing.current) return;
    setPoints((p) => [...p, [e.clientX, e.clientY]]);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const xs = points.map((p) => p[0]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    if (points.length < 3 || maxX - minX < 60) { setPoints([]); return; }
    const y = points.reduce((s, p) => s + p[1], 0) / points.length;
    setStraight({ x1: minX, x2: maxX, y });
    setTimeout(() => onDrawn(y), 450);
  };

  const d = points.map((p, i) => `${i ? "L" : "M"}${p[0]},${p[1]}`).join(" ");

  return (
    <div
      data-testid="draw-divider-overlay"
      className="fixed inset-0 z-50 cursor-crosshair touch-none select-none bg-black/20"
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
    >
      <div className={`absolute left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full bg-[#121212]/95 border border-[#27272A] pl-4 pr-1.5 py-1.5 text-xs text-[#E5E7EB] shadow-lg whitespace-nowrap transition-[top] ${barShown ? "top-14" : "top-3"}`} style={{ marginTop: "env(safe-area-inset-top)" }} data-testid="draw-divider-hint" onPointerDown={(e) => e.stopPropagation()}>
        <span className="h-2 w-2 rounded-full" style={{ background: RED }} />
        Draw a line where the new section starts
        <button type="button" data-testid="draw-divider-cancel" onClick={onCancel} className="ml-1 p-1 rounded-full hover:bg-[#27272A] text-[#9CA3AF] hover:text-white" aria-label="Cancel drawing">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <svg className="absolute inset-0 h-full w-full pointer-events-none">
        {d && (
          <path d={d} fill="none" stroke={RED} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
            style={{ opacity: straight ? 0 : 1, transition: "opacity 200ms ease-out" }} />
        )}
        {straight && (
          <line x1={straight.x1} x2={straight.x2} y1={straight.y} y2={straight.y} stroke={RED} strokeWidth="4" strokeLinecap="round"
            className="divider-snap" style={{ filter: "drop-shadow(0 0 6px rgba(239,68,68,0.7))" }} />
        )}
      </svg>
    </div>
  );
}
